import React, { useState, useEffect, useMemo, useContext } from 'react';

import { ColorTheme } from '../ColorTheme';
import { Markdown } from '../Markdown/Markdown.component';

import * as Styled from './DonationAskForm.styles';
import enUsDictionary from './DonationAskForm.dictionary.en-US';

import { getTwoToneTheming } from '+/styles/styledComponents/Form.styles';
import { Text } from '+/components/Text';
import { Button } from '+/components/Button';
import { PaymentMethodLogos } from '+/components/PaymentMethodLogos';
import * as FormStyled from '+/styles/styledComponents/Form.styles';
import { breakpoints } from '+/styles/themes/mainTheme';
import { debounce } from '+/utils/debounce';
import { addToDictionary, t } from '+/utils/textDictionary';
import { ICONS } from '+/utils/constants/icons';
import { UserContext } from '+/contexts/UserContext';

addToDictionary(enUsDictionary);

interface DonationAskFormProps {
  donationAskFormData: DonationAskFormComponent;
  advanceFormSequence?: () => void;
}

// The "Other" tile is always appended by the component; the CMS fields hold numbers only
const OTHER_OPTION = 'Other';

const isOtherOption = (option: string): boolean => option === OTHER_OPTION;

// cleanupData converts the suggested_donation_amounts_* fields to number arrays on real
// pages, but plain comma-separated strings still arrive from Storybook/tests
const parseAmounts = (amounts?: Array<number> | string): Array<string> => {
  if (Array.isArray(amounts)) return amounts.map(String);
  return (amounts || '')
    .split(',')
    .map(option => option.trim())
    .filter(Boolean);
};

const MORE_GIVING_OPTIONS_URL = 'https://thehumaneleague.org/ways-to-give';

export const DonationAskForm = ({
  donationAskFormData,
  advanceFormSequence,
}: DonationAskFormProps) => {
  const {
    header,
    headerVisibility,
    colorTheme,
    formBackgroundStyle,
    suggestedDonationAmountsOneTime,
    suggestedDonationAmountsRecurring,
    defaultDonationAmountOneTime,
    defaultDonationAmountRecurring,
    buttonText,
    donationFormUrl,
  } = donationAskFormData;

  const { accentedColorTheme, formColorTheme, formBaseShade } = getTwoToneTheming(
    formBackgroundStyle,
    colorTheme,
  );

  const defaultOptionFor = (isMonthlyFrequency: boolean): string => {
    const options = parseAmounts(
      isMonthlyFrequency ? suggestedDonationAmountsRecurring : suggestedDonationAmountsOneTime,
    );
    const defaultAmount = isMonthlyFrequency
      ? defaultDonationAmountRecurring
      : defaultDonationAmountOneTime;
    if (defaultAmount && options.includes(String(defaultAmount))) return String(defaultAmount);
    return options[0] || OTHER_OPTION;
  };

  const [selectedOption, setSelectedOption] = useState<string>(() => defaultOptionFor(false));
  const [customAmount, setCustomAmount] = useState('');
  const [isMonthly, setIsMonthly] = useState(false);

  const amountOptions = useMemo(
    () => [
      ...parseAmounts(
        isMonthly ? suggestedDonationAmountsRecurring : suggestedDonationAmountsOneTime,
      ),
      OTHER_OPTION,
    ],
    [isMonthly, suggestedDonationAmountsRecurring, suggestedDonationAmountsOneTime],
  );

  const amountRadioGroupName = `donation-ask-amounts-${donationAskFormData.id || 'preview'}`;
  const otherAmountInputRef = React.useRef<HTMLInputElement>(null);

  const handleCustomAmountChange = (event: React.FormEvent<HTMLInputElement>): void => {
    setCustomAmount((event.target as HTMLInputElement).value.replace(/[^0-9]/g, ''));
  };

  const selectOtherOption = (): void => {
    setSelectedOption(OTHER_OPTION);
    otherAmountInputRef.current?.focus();
  };

  const toggleIsMonthly = (): void => {
    const nextIsMonthly = !isMonthly;
    setIsMonthly(nextIsMonthly);
    setSelectedOption(defaultOptionFor(nextIsMonthly));
  };

  const selectedAmount = isOtherOption(selectedOption) ? customAmount : selectedOption;

  const { userData } = useContext(UserContext);

  // Query param format per everyaction-themes ONBOARDING.md:
  // one-time amounts use am + SelectedFrequency=0, monthly use recurringAm + SelectedFrequency=4
  const donationUrlWithParams: string = useMemo((): string => {
    // Need to pass in a base url (2nd arg) in case this is a relative link
    let donationUrlString = '';
    if (typeof window !== 'undefined' && donationFormUrl) {
      const donationUrl = new URL(donationFormUrl, window.location.origin);
      donationUrl.searchParams.set('SelectedFrequency', isMonthly ? '4' : '0');
      if (selectedAmount) {
        donationUrl.searchParams.set(isMonthly ? 'recurringAm' : 'am', selectedAmount);
      }
      donationUrl.searchParams.set('utm_medium', 'web');
      donationUrl.searchParams.set('utm_source', 'petition');
      const pageSlug = window.location.pathname.split('/').filter(Boolean).pop();
      if (pageSlug) {
        donationUrl.searchParams.set('ms', pageSlug);
      }
      donationUrlString = donationUrl.toString();
    }
    return donationUrlString;
  }, [donationFormUrl, selectedAmount, isMonthly]);

  // The donor's name/email are appended at navigation time, never to the rendered href —
  // GA4's outbound click event captures the anchor's href, which would leak PII into analytics
  const handleDonationLinkClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
    const { firstName, lastName, emailAddress } = userData || {};
    if (!donationUrlWithParams || !(firstName || lastName || emailAddress)) return;
    // Modified clicks (new tab/window) navigate via the href itself; leave those alone
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }
    event.preventDefault();
    const donationUrl = new URL(donationUrlWithParams);
    if (firstName) donationUrl.searchParams.set('fn', firstName);
    if (lastName) donationUrl.searchParams.set('ln', lastName);
    if (emailAddress) donationUrl.searchParams.set('em', emailAddress);
    window.location.assign(donationUrl.toString());
  };

  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth < breakpoints.lg.value : undefined,
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const debounceHandleResize = debounce(() => {
      setIsMobile(window.innerWidth < breakpoints.lg.value);
    }, 200);

    window.addEventListener('resize', debounceHandleResize);
    return () => {
      window.removeEventListener('resize', debounceHandleResize);
    };
  }, []);

  const isHeaderHidden =
    !header ||
    (isMobile && headerVisibility === 'desktop-only') ||
    (!isMobile && headerVisibility === 'mobile-only');

  return (
    <Styled.DonationAskForm isPadded={!isHeaderHidden}>
      {isHeaderHidden || (
        <ColorTheme colorTheme={accentedColorTheme}>
          <FormStyled.PreContent>
            <FormStyled.Container>
              <FormStyled.Header>
                <Markdown>{header}</Markdown>
              </FormStyled.Header>
            </FormStyled.Container>
          </FormStyled.PreContent>
        </ColorTheme>
      )}
      <ColorTheme colorTheme={formColorTheme}>
        <FormStyled.Content baseShade={!isHeaderHidden && formBaseShade}>
          <FormStyled.Container>
            <Text textItemData={donationAskFormData} />
            <Styled.DonationAmountOptions
              role="radiogroup"
              aria-label={t('DonationAskForm.AmountGroupLabel')}
              columns={Math.min(amountOptions.length, 4)}
            >
              {amountOptions.map(option =>
                isOtherOption(option) ? (
                  <Styled.OtherAmountLabel key={option} isSelected={selectedOption === option}>
                    <input
                      type="radio"
                      name={amountRadioGroupName}
                      value={option}
                      checked={selectedOption === option}
                      onChange={selectOtherOption}
                    />
                    <Styled.OtherLabelText>
                      {t('DonationAskForm.OtherOption')}
                    </Styled.OtherLabelText>
                    <Styled.OtherAmountPrefix aria-hidden>
                      {t('DonationAskForm.CurrencySymbol')}
                    </Styled.OtherAmountPrefix>
                    <Styled.OtherAmountInput
                      ref={otherAmountInputRef}
                      type="text"
                      inputMode="numeric"
                      aria-label={t('DonationAskForm.OtherAmountLabel')}
                      tabIndex={selectedOption === option ? 0 : -1}
                      value={customAmount}
                      onChange={handleCustomAmountChange}
                    />
                  </Styled.OtherAmountLabel>
                ) : (
                  <Styled.AmountLabel key={option} isSelected={selectedOption === option}>
                    <input
                      type="radio"
                      name={amountRadioGroupName}
                      value={option}
                      checked={selectedOption === option}
                      onChange={() => setSelectedOption(option)}
                    />
                    <Styled.AmountLabelContent hasSuffix={isMonthly}>
                      <Styled.AmountUnit>{t('DonationAskForm.CurrencySymbol')}</Styled.AmountUnit>
                      <span>{option}</span>
                      {isMonthly && (
                        <Styled.AmountSuffix>
                          {t('DonationAskForm.MonthlySuffix')}
                        </Styled.AmountSuffix>
                      )}
                    </Styled.AmountLabelContent>
                  </Styled.AmountLabel>
                ),
              )}
            </Styled.DonationAmountOptions>
            <Styled.MonthlyGiftLabel>
              <Styled.MonthlyGiftCheckbox
                type="checkbox"
                checked={isMonthly}
                onChange={toggleIsMonthly}
              />
              <Styled.MonthlyGiftLabelText>
                {t('DonationAskForm.MonthlyGiftText')}
              </Styled.MonthlyGiftLabelText>
            </Styled.MonthlyGiftLabel>
            <Styled.SubmitLink href={donationUrlWithParams} onClick={handleDonationLinkClick}>
              <Button big endIcon={ICONS.CARET_RIGHT}>
                {buttonText}
              </Button>
            </Styled.SubmitLink>
            <Styled.FooterLinks>
              <Styled.MoreGivingOptionsLink
                href={MORE_GIVING_OPTIONS_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={advanceFormSequence}
              >
                {t('DonationAskForm.MoreGivingOptions')}
              </Styled.MoreGivingOptionsLink>
              {advanceFormSequence && (
                <Styled.SkipButton type="button" onClick={advanceFormSequence}>
                  {t('DonationAskForm.SkipThis')}
                </Styled.SkipButton>
              )}
            </Styled.FooterLinks>
          </FormStyled.Container>
        </FormStyled.Content>
        <PaymentMethodLogos />
      </ColorTheme>
    </Styled.DonationAskForm>
  );
};
